/**
 * The sale calculator.
 *
 * This is the only place in the system where a sale total is worked out. The
 * checkout screen, the offline queue, the server and the reports all call it, so
 * an offline till and the back office can never disagree about a total.
 *
 * Prices are VAT-inclusive ILS agorot.
 */
import { type Minor, allocate, clampAtZero, multiply, percentOf, roundCash, sum } from './money.js';
import { type VatSplit, vatForSale } from './tax.js';
import { type Currency, toIls } from './currency.js';

export type DiscountType = 'none' | 'percent' | 'fixed';

export interface Discount {
  type: DiscountType;
  /** percent: 10 means 10%. fixed: an amount in agorot. */
  value: number;
  reason?: string;
  /** Set when a manager approved a discount above the cashier limit. */
  approvedByUserId?: string | null;
}

export const NO_DISCOUNT: Discount = { type: 'none', value: 0 };

export interface SaleLineInput {
  id: string;
  variantId: string;
  qty: number;
  /** VAT-inclusive unit price in agorot at the moment of the sale. */
  unitPrice: Minor;
  /** Weighted average cost per unit at the moment of the sale, for profit reports. */
  unitCost?: Minor;
  discount?: Discount;
  /** The promotion this line takes part in, and what it takes off (see promotions.ts). */
  promoId?: string | null;
  promoDiscount?: Minor;
  finalSale?: boolean;
  /** Negative-quantity lines are returns inside an exchange. */
  isReturn?: boolean;
}

export interface SaleLineTotals extends SaleLineInput {
  /** qty x unitPrice, before any discount. */
  gross: Minor;
  /** Taken off by a promotion - automatic, and not the cashier's discount. */
  promoDiscount: Minor;
  /** Discount put on this line by the cashier, on what is left after the promotion. */
  lineDiscount: Minor;
  /** This line's share of a whole-sale discount. */
  saleDiscountShare: Minor;
  /** What the customer actually pays for this line. Reports and returns use this. */
  net: Minor;
  /** Net divided by qty, rounded — the "price actually paid" for one piece. */
  netUnit: Minor;
}

export interface SaleTotals {
  lines: SaleLineTotals[];
  /** Everything at shelf price, before discounts. */
  gross: Minor;
  promoTotal: Minor;
  lineDiscountTotal: Minor;
  saleDiscountTotal: Minor;
  /** Promotions and discounts together. */
  discountTotal: Minor;
  /** What is owed before cash rounding. */
  total: Minor;
  vat: VatSplit;
  costTotal: Minor;
  itemCount: number;
}

function discountAmount(base: Minor, d: Discount | undefined): Minor {
  if (!d || d.type === 'none' || !d.value) return 0;
  if (d.type === 'percent') return clampAtZero(Math.min(percentOf(base, d.value), base));
  return clampAtZero(Math.min(Math.round(d.value), base));
}

export function computeSale(
  lines: SaleLineInput[],
  saleDiscount: Discount = NO_DISCOUNT,
  vatRate = 16,
): SaleTotals {
  const withLine = lines.map((l) => {
    const signedQty = l.isReturn ? -Math.abs(l.qty) : l.qty;
    const gross = multiply(l.unitPrice, signedQty);
    // Only something being bought takes part in a promotion, and never more than its price.
    const promoDiscount = gross > 0 ? clampAtZero(Math.min(Math.round(l.promoDiscount ?? 0), gross)) : 0;
    // A discount on a returned line mirrors the discount it was sold with.
    const lineDiscount =
      gross < 0 ? -discountAmount(-gross, l.discount) : discountAmount(gross - promoDiscount, l.discount);
    return { ...l, qty: signedQty, gross, promoDiscount, lineDiscount };
  });

  const gross = sum(withLine.map((l) => l.gross));
  const promoTotal = sum(withLine.map((l) => l.promoDiscount));
  const lineDiscountTotal = sum(withLine.map((l) => l.lineDiscount));
  const afterLine = gross - promoTotal - lineDiscountTotal;

  // A whole-sale discount only applies to what is being bought, never to returns.
  const positiveBase = sum(withLine.filter((l) => l.gross > 0).map((l) => l.gross - l.promoDiscount - l.lineDiscount));
  const saleDiscountTotal = discountAmount(positiveBase, saleDiscount);

  // Spread it over the buying lines so every agora lands somewhere.
  const weights = withLine.map((l) => (l.gross > 0 ? l.gross - l.promoDiscount - l.lineDiscount : 0));
  const shares = allocate(saleDiscountTotal, weights);

  const outLines: SaleLineTotals[] = withLine.map((l, i) => {
    const net = l.gross - l.promoDiscount - l.lineDiscount - shares[i];
    return {
      ...l,
      saleDiscountShare: shares[i],
      net,
      netUnit: l.qty === 0 ? 0 : Math.round(net / l.qty),
    };
  });

  const total = afterLine - saleDiscountTotal;

  return {
    lines: outLines,
    gross,
    promoTotal,
    lineDiscountTotal,
    saleDiscountTotal,
    discountTotal: promoTotal + lineDiscountTotal + saleDiscountTotal,
    total,
    vat: vatForSale(total, vatRate),
    costTotal: sum(outLines.map((l) => multiply(l.unitCost ?? 0, l.qty))),
    itemCount: outLines.reduce((a, l) => a + l.qty, 0),
  };
}

// ---------------------------------------------------------------------------
// Payments
// ---------------------------------------------------------------------------

/**
 * How a sale was settled.
 *
 * `credit` is a credit note the SHOP owes the customer, spent against a sale.
 * `tab` is the opposite: the customer owes the shop, and walks out without
 * paying. Neither moves money today; both still settle the bill, which is why
 * settle() treats them like any other non-cash payment.
 */
/*
 * How a sale was paid for.
 *
 * `credit` is money the shop OWES the customer - granted after a return, or as
 * a goodwill gesture. `gift` is money the customer ALREADY PAID, on a card
 * bought earlier. They spend identically and they are not the same thing: one
 * cost the shop nothing and the other was cash through the door last month.
 * `tab` is the opposite of both - money the customer owes the shop.
 *
 * Adding a member here means every place that names a method must answer for
 * it. Those are `paymentLabel` (screens) and `paymentName` (receipts); both are
 * a switch with a default, so a member added later shows as itself rather than
 * quietly borrowing the name of whichever branch happened to be last.
 */
export type PaymentMethod = 'cash' | 'card' | 'credit' | 'gift' | 'tab' | 'other';

/** Tenders that are not cash in the drawer, and so can never hand cash back. */
export const NON_CASH_METHODS: readonly PaymentMethod[] = ['card', 'credit', 'gift', 'tab'] as const;

export interface PaymentInput {
  id: string;
  method: PaymentMethod;
  /** The name the owner gave a custom method, e.g. "Jawwal Pay". */
  methodLabel?: string;
  currency: string;
  /** What the customer handed over, in that currency's minor units. */
  amount: Minor;
  /** The rate used, x 1,000,000. Frozen onto the sale forever. */
  rateMicros: number;
  decimals: number;
  reference?: string;
}

export interface Settlement {
  /** The sale total before cash rounding. */
  total: Minor;
  /** Rounding applied to the cash part only (card is never rounded). */
  rounding: Minor;
  /** What the customer owes once rounding is applied. */
  payable: Minor;
  /** Everything handed over, converted to ILS. */
  paidIls: Minor;
  /** Positive means change is owed to the customer, in ILS. */
  change: Minor;
  /** Positive means the customer still owes this much. */
  remaining: Minor;
  paymentsIls: { id: string; ils: Minor }[];
  settled: boolean;
}

export function paymentToIls(p: PaymentInput): Minor {
  return toIls(p.amount, { decimals: p.decimals, rateMicros: p.rateMicros });
}

/**
 * Work out change and what is still owed.
 *
 * Cash rounding rule: only the part of the bill actually being paid in cash is
 * rounded to the nearest 0.10 ILS. Pay by card and you pay the exact amount.
 */
export function settle(
  total: Minor,
  payments: PaymentInput[],
  opts: { cashStepAgorot?: number } = {},
): Settlement {
  const step = opts.cashStepAgorot ?? 10;
  const paymentsIls = payments.map((p) => ({ id: p.id, ils: paymentToIls(p) }));
  const byId = new Map(paymentsIls.map((p) => [p.id, p.ils]));

  // The 10-agorot rule exists because those shekel coins no longer exist. It
  // has nothing to say about dollars or dinars: a sale paid entirely in foreign
  // cash used to get its total rounded to a shekel coin anyway, and the dollars
  // handed over then fell a few agorot short of it on every such sale.
  const isIlsCash = (p: PaymentInput) => p.method === 'cash' && p.currency === 'ILS';
  const otherIls = sum(payments.filter((p) => !isIlsCash(p)).map((p) => byId.get(p.id) ?? 0));
  const hasIlsCash = payments.some(isIlsCash);

  let rounding = 0;
  if (hasIlsCash) {
    const cashDue = total - otherIls;
    if (cashDue > 0) rounding = roundCash(cashDue, step) - cashDue;
  }

  const payable = total + rounding;
  const paidIls = sum(paymentsIls.map((p) => p.ils));
  const diff = paidIls - payable;

  return {
    total,
    rounding,
    payable,
    paidIls,
    change: diff > 0 ? diff : 0,
    remaining: diff < 0 ? -diff : 0,
    paymentsIls,
    settled: diff >= 0,
  };
}

/** Change handed back in cash is itself rounded to a coin that exists. */
export function changeInCash(change: Minor, cashStepAgorot = 10): Minor {
  return roundCash(change, cashStepAgorot);
}

/** What the customer should hand over if paying the whole bill in one foreign currency. */
export function dueInCurrency(total: Minor, cur: Currency): Minor {
  const exact = (total * 10 ** cur.decimals * 1_000_000) / (cur.rateMicros * 100);
  const step = cur.cashStep || 1;
  return Math.ceil(exact / step) * step;
}

/** What the cashier's whole-sale discount is worked out on: after promotions and line discounts. */
export function saleDiscountBase(t: Pick<SaleTotals, 'lines'>): Minor {
  return sum(t.lines.filter((l) => l.gross > 0).map((l) => l.gross - l.promoDiscount - l.lineDiscount));
}
